import { __assign } from 'tslib';
import React__default, { useState, useRef, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import Cropper from 'cropperjs';
import { CropperJSCSS } from './cropperjs.styled.js';

// Custom point size in pixel in order to have crop points more easily clickable / targetable
var POINT_SIZE_PX = 7;
var MANDATORY_IMG_STYLES_FOR_CROPPERJS_LIB = {
    display: "block",
    /* This rule is very important, please don't ignore this */
    maxWidth: "100%"
};
var KCrop = function (_a) {
    var className = _a.className, id = _a.id, _b = _a.testId, testId = _b === void 0 ? "KCrop" : _b, imgSrc = _a.imgSrc, imgAlt = _a.imgAlt, sx = _a.sx, initialCrop = _a.initialCrop, aspectRatio = _a.aspectRatio, onCropChange = _a.onCropChange, onReady = _a.onReady, viewMode = _a.viewMode, zoomable = _a.zoomable, rotatable = _a.rotatable, toggleDragModeOnDblclick = _a.toggleDragModeOnDblclick, background = _a.background;
    var _c = useState(false), cropperIsReady = _c[0], setCropperIsReady = _c[1];
    var imgRef = useRef(null);
    var cropperRef = useRef();
    var handleReady = useCallback(function (event) {
        setCropperIsReady(true);
        onReady && onReady(event.currentTarget.cropper);
        initialCrop && cropperRef.current && cropperRef.current.setData(initialCrop);
    }, [initialCrop, onReady]);
    useEffect(function () {
        // if ref image is not valued OR cropper already valued => return
        if (!imgRef.current || cropperRef.current) {
            return;
        }
        cropperRef.current = new Cropper(imgRef.current, {
            viewMode: viewMode,
            aspectRatio: aspectRatio,
            ready: handleReady,
            zoomable: zoomable,
            rotatable: rotatable,
            toggleDragModeOnDblclick: toggleDragModeOnDblclick,
            background: background
        });
    }, [
        handleReady,
        aspectRatio,
        viewMode,
        zoomable,
        rotatable,
        toggleDragModeOnDblclick,
        background,
        initialCrop,
    ]);
    // we use a useEffect to manage aspectRatio change (and not during the construction of the cropperjs component)
    // in order to be able to update the "aspectRatio" function without updating all component options
    useEffect(function () {
        var _a;
        if (!cropperRef.current) {
            return;
        }
        (_a = cropperRef.current) === null || _a === void 0 ? void 0 : _a.setAspectRatio(aspectRatio || NaN);
    }, [aspectRatio]);
    // we use a useEffect to manage initialCrop change (and not during the construction of the cropperjs component)
    // in order to be able to update the "initialCrop" function without updating all component options
    useEffect(function () {
        if (!cropperRef.current) {
            return;
        }
        initialCrop && cropperRef.current.setData(initialCrop);
    }, [initialCrop]);
    // component unmounts => destroy cropper
    useEffect(function () {
        return function () {
            cropperRef.current && cropperRef.current.destroy();
            cropperRef.current = undefined;
            setCropperIsReady(false);
        };
    }, []);
    // we use a useEffect to record the crop event (and not during the construction of the cropperjs component)
    // in order to be able to update the "onCropChange" function without destroying the component
    useEffect(function () {
        if (!onCropChange) {
            return;
        }
        var currentImgRef = imgRef.current;
        var cropListener = function (ev) { return onCropChange(ev.detail); };
        currentImgRef === null || currentImgRef === void 0 ? void 0 : currentImgRef.addEventListener("crop", cropListener);
        return function () { return currentImgRef === null || currentImgRef === void 0 ? void 0 : currentImgRef.removeEventListener("crop", cropListener); };
    }, [onCropChange]);
    // we use a useEffect to listen for img changes : we need to reset and clear cropperjs (to have ready state and compute new crop and image size)
    useEffect(function () {
        if (!cropperRef.current) {
            return;
        }
        setCropperIsReady(false);
        cropperRef.current.reset().clear().replace(imgSrc);
    }, [imgSrc]);
    return (React__default.createElement(CropperJSCSS, { id: id, "data-testid": testId, className: clsx("KCrop", className), sx: __assign(__assign({}, sx), { transition: "opacity .3s", opacity: cropperIsReady ? 1 : 0, "& .cropper-point": {
                padding: "".concat(POINT_SIZE_PX, "px")
            }, "& .cropper-point.point-e": {
                right: "-".concat(POINT_SIZE_PX, "px"),
                marginTop: "-".concat(POINT_SIZE_PX, "px")
            }, "& .cropper-point.point-n": {
                top: "-".concat(POINT_SIZE_PX, "px"),
                marginLeft: "-".concat(POINT_SIZE_PX, "px")
            }, "& .cropper-point.point-w": {
                left: "-".concat(POINT_SIZE_PX, "px"),
                marginTop: "-".concat(POINT_SIZE_PX, "px")
            }, "& .cropper-point.point-s": {
                bottom: "-".concat(POINT_SIZE_PX, "px"),
                marginLeft: "-".concat(POINT_SIZE_PX, "px")
            }, "& .cropper-point.point-ne": {
                right: "-".concat(POINT_SIZE_PX, "px"),
                top: "-".concat(POINT_SIZE_PX, "px")
            }, "& .cropper-point.point-nw": {
                left: "-".concat(POINT_SIZE_PX, "px"),
                top: "-".concat(POINT_SIZE_PX, "px")
            }, "& .cropper-point.point-sw": {
                bottom: "-".concat(POINT_SIZE_PX, "px"),
                left: "-".concat(POINT_SIZE_PX, "px")
            }, "& .cropper-point.point-se": {
                bottom: "-".concat(POINT_SIZE_PX, "px"),
                right: "-".concat(POINT_SIZE_PX, "px")
            } }) },
        React__default.createElement("img", { ref: imgRef, src: imgSrc, alt: imgAlt, style: MANDATORY_IMG_STYLES_FOR_CROPPERJS_LIB })));
};

export { KCrop as default };
//# sourceMappingURL=index.js.map
